import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, Loader2, BookOpen, Mail, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Map product_id to human-readable name
const PRODUCT_NAMES = {
  book1: 'First Steps: Foundations of Effective IT Leadership',
  book2: 'Stepping Up: Turning Peer Relationships into Leadership Success',
  book1_ai_bundle_monthly: 'First Steps + Managerial Companion (Monthly)',
  book1_ai_bundle_annual: 'First Steps + Managerial Companion (Annual)',
  book1_ai_bundle: 'First Steps + Managerial Companion',
  book1_ai_upsell_monthly: 'First Steps + Managerial Companion (Monthly)',
  book1_ai_upsell_annual: 'First Steps + Managerial Companion (Annual)',
  book1_ai_upsell: 'First Steps + Managerial Companion',
  book1_ai: 'First Steps + Managerial Companion',
  book2_ai_bundle_monthly: 'Stepping Up + Managerial Companion (Monthly)',
  book2_ai_bundle_annual: 'Stepping Up + Managerial Companion (Annual)',
  series_bundle: 'Complete 8-Book Series Bundle',
  series_bundle_ai: 'Complete Series + Managerial Companion (Annual)',
  ai_monthly: 'Managerial Companion (Monthly)',
  ai_annual: 'Managerial Companion (Annual)',
  core_monthly: 'Companion Core (Monthly)',
  core_annual: 'Companion Core (Annual)',
  pro_monthly: 'Companion Pro (Monthly)',
  pro_annual: 'Companion Pro (Annual)',
  starter_free: 'Companion Free (Starter)',
};

const isCompanionOnly = (productId) => ['ai_monthly', 'ai_annual', 'core_monthly', 'core_annual', 'pro_monthly', 'pro_annual'].includes(productId);
const includesBook = (productId) => !isCompanionOnly(productId);
const includesCompanion = (productId) => productId !== 'book1' && productId !== 'series_bundle';

const PaymentStatus = ({ sessionId, onClose }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const checkPaymentStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      const data = await response.json();

      if (response.ok) {
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return true;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return false;
    }
  }, [sessionId]);

  useEffect(() => {
    const pollStatus = async () => {
      const isDone = await checkPaymentStatus();
      
      if (!isDone && attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      } else if (!isDone) {
        setStatus('failed');
      }
    };

    if (sessionId) {
      pollStatus();
    }
  }, [sessionId, checkPaymentStatus, attempts]);

  // Clear URL params after displaying status
  useEffect(() => {
    if (status !== 'checking') {
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [status]);

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Confirming Your Payment
            </h3>
            <p className="text-slate-500">
              Please wait while we verify your purchase...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    const productId = paymentData?.product_id || '';
    const productName = PRODUCT_NAMES[productId] || productId;
    const hasBook = includesBook(productId);
    const hasCompanion = includesCompanion(productId);
    const companionOnly = isCompanionOnly(productId);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              {companionOnly ? 'Welcome to Companion!' : 'Payment Successful!'}
            </h3>
            <p className="text-slate-600 mb-6">
              {companionOnly
                ? 'Your Managerial Companion access is now active.'
                : 'Thank you for your purchase. Your order has been confirmed.'}
            </p>
            
            {paymentData && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">Product:</span>
                  <span className="font-medium text-slate-900">{productName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Amount:</span>
                  <span className="font-medium text-slate-900">
                    {paymentData.amount === 0 ? 'Free' : `£${paymentData.amount?.toFixed(2)} ${paymentData.currency?.toUpperCase()}`}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {hasBook && (
                <>
                  <div className="flex items-center gap-3 text-left text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span>A confirmation email with your download link will arrive shortly.</span>
                  </div>
                  <div className="flex items-center gap-3 text-left text-sm text-slate-600 bg-green-50 p-3 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Your book will be available for immediate download.</span>
                  </div>
                </>
              )}
              {hasCompanion && (
                <div className="flex items-center gap-3 text-left text-sm text-slate-600 bg-purple-50 p-3 rounded-lg">
                  <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  <span>
                    {companionOnly
                      ? 'Log in to your dashboard to start using your AI management coach.'
                      : 'Your Managerial Companion AI access is now active.'}
                  </span>
                </div>
              )}
              {companionOnly && (
                <div className="flex items-center gap-3 text-left text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span>Check your email for a welcome message with tips to get started.</span>
                </div>
              )}
            </div>

            <Button 
              className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                if (companionOnly) {
                  onClose();
                  navigate('/dashboard');
                } else {
                  onClose();
                }
              }}
              data-testid="payment-success-close"
            >
              {companionOnly ? 'Go to Dashboard' : 'Continue'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Session Expired
            </h3>
            <p className="text-slate-600 mb-6">
              Your checkout session has expired. Please try again.
            </p>
            <Button 
              className="w-full"
              onClick={onClose}
              data-testid="payment-expired-close"
            >
              Back to Pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed state
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Payment Verification Failed
          </h3>
          <p className="text-slate-600 mb-6">
            We couldn't verify your payment. If you were charged, please contact support.
          </p>
          <Button 
            className="w-full"
            onClick={onClose}
            data-testid="payment-failed-close"
          >
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentStatus;
